// `RelanceRow` — single pending-relance row inside `PendingQueuePage`
// (US-165 gap-design + US-164 cancellation surface).
//
// Layout (left → right):
//   - Avatar (contact initials / avatar URL, identity colour)
//   - Contact name + role / company • "Catch-up • Attempt 1"
//   - Scheduled time chip
//   - Body draft preview (one-paragraph, italic when empty)
//   - Action cluster: Send now • Skip • Snooze
//
// When the row is `cancelled_*` we replace the action cluster with the
// `CancellationBanner` (US-164). The "Open conversation" link sits as a
// secondary anchor so users can jump back to the inbox thread to inspect
// the lead in full.

import { Link } from '@tanstack/react-router';
import { useCallback } from 'react';

import { useLocale, useT } from '@barel/i18n';
import type { PendingRelanceRow } from '@barel/api-client/react';
import { Avatar, Badge, Banner, Button } from '@barel/ui';

import { formatDate } from '@/lib/format';

import { CancellationBanner, reasonFromState, type CancellationReason } from './CancellationBanner';
import { SnoozeMenu } from './SnoozeMenu';

export interface RelanceRowProps {
  row: PendingRelanceRow;
  /** Caller wires the mutation; we just bubble up the user's intent. */
  onApprove: (id: string) => void;
  onRetry: (id: string) => void;
  onSkip: (id: string) => void;
  onSnooze: (id: string, snoozeUntilIso: string) => void;
  /** Disable every action button while a sibling mutation is in flight. */
  busy?: boolean;
  /** When the row is reflected as a `cancelled_*` realtime event. */
  cancelledOverride?: CancellationReason | null;
}

const TYPE_LABEL_KEYS: Record<string, string> = {
  catchup: 'queue.types.catchup',
  project: 'queue.types.project',
  lastShot: 'queue.types.lastShot',
};

function formatTime(iso: string, locale: string): string {
  try {
    return formatDate(new Date(iso), { locale, hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

export function RelanceRow({
  row,
  onApprove,
  onRetry,
  onSkip,
  onSnooze,
  busy,
  cancelledOverride,
}: RelanceRowProps) {
  const t = useT('relances');
  const locale = useLocale();
  const intlTag = locale === 'fr' ? 'fr-FR' : 'en-US';

  const typeLabelKey = TYPE_LABEL_KEYS[row.typeKind] ?? 'queue.types.custom';
  const typeLabel = t(typeLabelKey);
  const scheduledTime = formatTime(row.scheduledFor, intlTag);

  const cancellationReason = cancelledOverride ?? reasonFromState(row.state);

  const handleApprove = useCallback(() => onApprove(row.id), [onApprove, row.id]);
  const handleRetry = useCallback(() => onRetry(row.id), [onRetry, row.id]);
  const handleSkip = useCallback(() => onSkip(row.id), [onSkip, row.id]);
  const handleSnooze = useCallback((iso: string) => onSnooze(row.id, iso), [onSnooze, row.id]);

  const draft = row.bodyDraft?.trim() ?? '';

  return (
    <article
      data-component="RelanceRow"
      data-testid={`relance-row-${row.id}`}
      data-state={cancellationReason ? 'cancelled' : row.state}
      className="rounded-xl border border-default bg-bg-surface p-4"
    >
      <div className="flex items-start gap-3">
        <Avatar
          size="md"
          alt={row.conversation.contactName}
          initials={row.conversation.contactInitials}
          src={row.conversation.contactAvatarUrl ?? undefined}
          tone={row.conversation.contactColor ? 'identity' : 'neutral'}
          identityColor={row.conversation.contactColor ?? undefined}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-sm font-semibold text-primary">
              {row.conversation.contactName}
            </span>
            {row.conversation.contactRole || row.conversation.contactCompany ? (
              <span className="text-xs text-tertiary">
                {[row.conversation.contactRole, row.conversation.contactCompany]
                  .filter(Boolean)
                  .join(' • ')}
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            <Badge
              tone="accent"
              variant="soft"
              size="sm"
              data-testid={`relance-row-${row.id}-type`}
            >
              {typeLabel}
            </Badge>
            {row.attemptNumber > 1 ? (
              <Badge tone="neutral" variant="soft" size="sm">
                {t('queue.attemptLabel', { n: row.attemptNumber })}
              </Badge>
            ) : null}
            <span className="text-xs text-secondary">
              {t('queue.scheduledAt', { time: scheduledTime })}
            </span>
          </div>
        </div>
        <Link
          to="/inbox"
          search={(prev) => ({
            ...prev,
            accountId: prev.accountId,
            mode: prev.mode,
            selectedId: row.conversation.id,
          })}
          className="text-2xs font-semibold text-accent hover:underline"
          data-testid={`relance-row-${row.id}-open`}
        >
          {t('queue.openConversation')}
        </Link>
      </div>

      <div className="mt-3 border-t border-subtle pt-3">
        <div className="text-2xs font-semibold uppercase tracking-wider text-secondary">
          {t('queue.draftLabel')}
        </div>
        {draft ? (
          <p
            className="mt-1 whitespace-pre-wrap text-sm text-primary leading-relaxed"
            data-testid={`relance-row-${row.id}-draft`}
          >
            {draft}
          </p>
        ) : (
          <p className="mt-1 text-sm italic text-tertiary leading-relaxed">
            {t('queue.draftEmpty')}
          </p>
        )}
      </div>

      {cancellationReason ? (
        <div className="mt-3">
          <CancellationBanner
            reason={cancellationReason}
            data-testid={`relance-row-${row.id}-cancellation`}
          />
        </div>
      ) : row.state === 'errored' ? (
        <div className="mt-3 flex flex-col gap-3">
          <Banner
            tone="error"
            title={t('queue.failed.title')}
            data-testid={`relance-row-${row.id}-error`}
          >
            {t('queue.failed.body')}
          </Banner>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              disabled={busy}
              data-testid={`relance-row-${row.id}-skip`}
            >
              {t('queue.skip')}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleRetry}
              disabled={busy}
              data-testid={`relance-row-${row.id}-retry`}
            >
              {t('queue.retry')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            disabled={busy}
            data-testid={`relance-row-${row.id}-skip`}
          >
            {t('queue.skip')}
          </Button>
          <SnoozeMenu
            onSnooze={handleSnooze}
            disabled={busy}
            data-testid={`relance-row-${row.id}-snooze`}
          />
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleApprove}
            disabled={busy}
            data-testid={`relance-row-${row.id}-approve`}
          >
            {t('queue.approve')}
          </Button>
        </div>
      )}
    </article>
  );
}
